/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from "react";
import { Box, Skeleton, Stack } from "@mui/material";
import { debug as debugFn } from "debug";
import { Formik, useFormikContext } from "formik";
import { isEqual } from "lodash-es";
import { useTranslation } from "react-i18next";
import type { z, ZodType } from "zod";

import { toFormikValidationSchema } from "@thalamos/common";
import type {
  FormBuilderField,
  IncidentWorkItemWithContext,
  TranslationInput,
} from "@thalamos/vision-common";
import {
  buildTranslationInput,
  tk,
  translate,
  treatInvalidValuesAsUndefined,
} from "@thalamos/vision-common";

import { FormWrapper } from "../../../layout/FormWrapper.js";
import { Field } from "../Field.js";
import type { AutoSaveProps } from "./AutoSave.js";
import { AutoSave } from "./AutoSave.js";
import { Footer, FooterSkeleton } from "./Footer.js";
import { Header, HeaderSkeleton } from "./Header.js";
import type { TranslationContext } from "./TranslationContext.js";

const debug = debugFn("vision-frontend:MultiPageForm");

/**
 * Watches the values of fields changing and resets fields to the
 * unset value ("") if they are not visible.
 */
function FieldValueNormalizer<
  Schema extends z.ZodType<any>,
  Form extends Record<string, unknown>,
>({
  incidentWorkItem,
  strictFieldSchemas,
  fields,
}: {
  incidentWorkItem?: IncidentWorkItemWithContext;
  strictFieldSchemas: Record<string, ZodType<any>>;

  fields: Array<FormBuilderField<Form, any>>;
}) {
  const { values, setValues } = useFormikContext<z.infer<Schema>>();

  useEffect(() => {
    // Figure out which fields are not visible based on the latest values
    const hiddenFields = fields.filter(
      (field) =>
        !(
          field.visibilityFn?.({
            formValues: treatInvalidValuesAsUndefined(
              strictFieldSchemas,
              values,
            ),
            incidentWorkItem,
          }) ?? true
        ),
    );

    // For any fields which are not visible, set the unset value ("")
    const newValues = {
      ...values,
      ...Object.fromEntries(hiddenFields.map((field) => [field.fieldName, ""])),
    };

    if (!isEqual(values, newValues)) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      void setValues(newValues);
    }
  }, [fields, setValues, strictFieldSchemas, values, incidentWorkItem]);

  return null;
}

/**
 * Helper function to compute form visibility based on the current form values.
 * Returns both the visible fields across the entire form and which pages are visible.
 */
function computeFormVisibility<Schema extends z.ZodType<any>>(
  pages: { fields: readonly FormBuilderField<z.infer<Schema>, any>[] }[],
  strictFieldSchemas: Record<string, z.ZodType<any>>,
  values: z.infer<Schema>,
  incidentWorkItem?: IncidentWorkItemWithContext,
): {
  visibleFieldsOnForm: FormBuilderField<z.infer<Schema>, any>[];
  visiblePages: boolean[];
} {
  const allFields = pages.flatMap((page) => page.fields);

  // What fields are visible based on the branching rules and current values?
  const visibleFieldsOnForm = allFields.filter(
    (field) =>
      field.visibilityFn?.({
        formValues: treatInvalidValuesAsUndefined(strictFieldSchemas, values),
        incidentWorkItem,
      }) ?? true,
  );

  const visibleFieldNames = new Set(
    visibleFieldsOnForm.map((field) => field.fieldName),
  );

  // What pages are visible based on the visible fields?
  const visiblePages = pages.map((page) =>
    page.fields.some((field) => visibleFieldNames.has(field.fieldName)),
  );

  return { visibleFieldsOnForm, visiblePages };
}

/**
 * Helper function to compute which pages are visible based on the current form values.
 */
function computeVisiblePages<Schema extends z.ZodType<any>>(
  pages: { fields: readonly FormBuilderField<z.infer<Schema>, any>[] }[],
  strictFieldSchemas: Record<string, z.ZodType<any>>,
  values: z.infer<Schema>,
  incidentWorkItem?: IncidentWorkItemWithContext,
): boolean[] {
  return computeFormVisibility(
    pages,
    strictFieldSchemas,
    values,
    incidentWorkItem,
  ).visiblePages;
}

/**
 * Component that handles skipping invisible initial pages on mount.
 * If the first page is invisible, it finds the first visible page and navigates there.
 * If all pages are invisible, it triggers form submission immediately.
 */
function InitialPageSkipper<Schema extends z.ZodType<any>>({
  pages,
  strictFieldSchemas,
  incidentWorkItem,
  pageIndex,
  setPageIndex,
  hasSkippedInitialPages,
  onSubmitWithValues,
}: {
  pages: { fields: readonly FormBuilderField<z.infer<Schema>, any>[] }[];
  strictFieldSchemas: Record<string, z.ZodType<any>>;
  incidentWorkItem?: IncidentWorkItemWithContext;
  pageIndex: number;
  setPageIndex: (index: number) => void;
  hasSkippedInitialPages: RefObject<boolean>;
  onSubmitWithValues: (values: z.infer<Schema>) => Promise<void>;
}) {
  const { values } = useFormikContext<z.infer<Schema>>();

  useEffect(() => {
    // Only run once on initial mount, before any user interaction
    if (hasSkippedInitialPages.current || pageIndex !== 0) {
      return;
    }

    const visiblePages = computeVisiblePages(
      pages,
      strictFieldSchemas,
      values,
      incidentWorkItem,
    );

    // If the first page is already visible, nothing to do
    if (visiblePages[0]) {
      hasSkippedInitialPages.current = true;
      return;
    }

    // Find the first visible page
    const firstVisiblePageIndex = visiblePages.findIndex(
      (isVisible) => isVisible,
    );

    if (firstVisiblePageIndex === -1) {
      // All pages are invisible - submit immediately with initial values
      debug("All pages are invisible, submitting immediately");
      hasSkippedInitialPages.current = true;
      void onSubmitWithValues(values);
    } else {
      // Skip to the first visible page
      debug(
        `First page is invisible, skipping to page ${String(firstVisiblePageIndex)}`,
      );
      hasSkippedInitialPages.current = true;
      setPageIndex(firstVisiblePageIndex);
    }
  }, [
    pages,
    strictFieldSchemas,
    values,
    incidentWorkItem,
    pageIndex,
    setPageIndex,
    hasSkippedInitialPages,
    onSubmitWithValues,
  ]);

  return null;
}

export type MultiPageFormProps<Schema extends z.ZodType<any>> = {
  headerText: TranslationInput<TranslationContext>;
  incidentWorkItem?: IncidentWorkItemWithContext;
  schema: Schema;
  strictFieldSchemas: Record<string, z.ZodType<any>>;
  initialValues: z.infer<Schema>;

  pages: { fields: readonly FormBuilderField<z.infer<Schema>, any>[] }[];
  onCancel: () => void;
  onAutoSave?: AutoSaveProps<Schema>["onAutoSave"];
  onSubmit: (value: z.infer<Schema>) => Promise<void>;
  onBack?: () => void;
};

export function MultiPageForm<Schema extends z.ZodType<any>>(
  props: MultiPageFormProps<Schema>,
) {
  return <MultiPageFormInner<Schema> type={"loaded"} {...props} />;
}

///

export type MultiPageFormSkeletonProps<Schema extends z.ZodType<any>> = Pick<
  MultiPageFormProps<Schema>,
  "headerText"
>;

export function MultiPageFormSkeleton<Schema extends z.ZodType<any>>({
  headerText,
}: MultiPageFormSkeletonProps<Schema>) {
  return (
    <MultiPageFormInner<Schema> type={"loading"} headerText={headerText} />
  );
}

///

export type MultiPageFormInnerProps<Schema extends z.ZodType<any>> =
  | ({ type: "loading" } & MultiPageFormSkeletonProps<Schema>)
  | ({
      type: "loaded";
    } & MultiPageFormProps<Schema>);

export function MultiPageFormInner<Schema extends z.ZodType<any>>(
  props: MultiPageFormInnerProps<Schema>,
) {
  const { t } = useTranslation();

  // Track which page we are on
  const [pageIndex, setPageIndex] = useState(0);
  const [visitedPageIndexes, setVisitedPageIndexes] = useState<number[]>([]);

  // Track whether we've already handled skipping invisible initial pages
  const hasSkippedInitialPages = useRef(false);

  const onCleanupAndSubmit = useCallback(
    async (values: z.infer<Schema>) => {
      if (props.type === "loaded") {
        await props.onSubmit(props.schema.parse(values));
      }
    },
    [props],
  );

  const onFirstPage = visitedPageIndexes.length === 0;

  const onPageBack = useCallback(() => {
    if (props.type === "loaded" && !onFirstPage) {
      // Pop the last value off the stack
      // Don't use Array.pop() as we don't want to mutate the React state directly
      const targetPageIndex = visitedPageIndexes.at(-1);
      if (targetPageIndex === undefined) return;
      setVisitedPageIndexes(
        visitedPageIndexes.slice(0, visitedPageIndexes.length - 1),
      );
      setPageIndex(targetPageIndex);
    }
  }, [onFirstPage, props.type, visitedPageIndexes]);

  // A ref to portal in debug components for autosave
  const autoSaveDebugRef = useRef<HTMLDivElement>(null);

  const translationContext =
    props.type === "loading"
      ? { t }
      : { t, incidentWorkItem: props.incidentWorkItem };

  return (
    <FormWrapper>
      {props.type === "loading" ? (
        <HeaderSkeleton
          title={buildTranslationInput(props.headerText, translationContext)}
        />
      ) : (
        <Header
          title={buildTranslationInput(props.headerText, translationContext)}
          page={pageIndex}
          totalPages={props.pages.length}
          onBack={onFirstPage ? props.onBack : onPageBack}
        />
      )}
      {props.type === "loading" ? (
        <Stack
          sx={{
            gap: "0.25rem",
            marginTop: "2rem",
          }}
        >
          <Skeleton variant="text" width={"50%"} />
          <Skeleton variant="rectangular" height={55} />
          <Box sx={{ height: "2rem" }} />
          <FooterSkeleton />
        </Stack>
      ) : (
        <Formik<z.infer<Schema>>
          validationSchema={toFormikValidationSchema(props.schema)}
          validateOnChange={true}
          initialValues={props.initialValues}
          onSubmit={onCleanupAndSubmit}
        >
          {(formikProps) => {
            const values = formikProps.values;
            const allFields = props.pages.flatMap((page) => page.fields);

            // Compute which fields and pages are visible based on branching rules
            const { visibleFieldsOnForm, visiblePages } = computeFormVisibility(
              props.pages,
              props.strictFieldSchemas,
              values,
              props.incidentWorkItem,
            );

            const visibleFieldNames = new Set(
              visibleFieldsOnForm.map((field) => field.fieldName),
            );

            const currentPage = props.pages[pageIndex];
            if (currentPage === undefined) {
              throw new Error(`No form page at index ${String(pageIndex)}`);
            }
            const fieldsOnPage = currentPage.fields;
            const visibleFieldsOnPage = fieldsOnPage.filter((field) =>
              visibleFieldNames.has(field.fieldName),
            );

            // The current page has many fields on it, and we want to autofocus the first field
            // that the user can interact with. We skip fields that are read-only (banners etc)
            // since there's no point in focusing on them. We don't bother running the isEnabled
            // check here, because the top-down order of the fields means that the first non-readonly
            // field will always be invisible or enabled.
            const indexOfFirstVisibleWritableField =
              visibleFieldsOnPage.findIndex(
                (field) => !(field.isReadOnly ?? false),
              );

            return (
              <Stack
                role="form"
                data-testid={`multiPageForm-page-${String(pageIndex)}`}
                aria-label={translate(
                  t,
                  buildTranslationInput(props.headerText, translationContext),
                )}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "1.875rem",
                  marginTop: "2rem",
                  justifyContent: "space-between",
                  minHeight: "100%",
                }}
              >
                <FieldValueNormalizer<Schema, z.infer<Schema>>
                  incidentWorkItem={props.incidentWorkItem}
                  fields={allFields}
                  strictFieldSchemas={props.strictFieldSchemas}
                />
                <InitialPageSkipper<Schema>
                  pages={props.pages}
                  strictFieldSchemas={props.strictFieldSchemas}
                  incidentWorkItem={props.incidentWorkItem}
                  pageIndex={pageIndex}
                  setPageIndex={setPageIndex}
                  hasSkippedInitialPages={hasSkippedInitialPages}
                  onSubmitWithValues={onCleanupAndSubmit}
                />
                {visibleFieldsOnPage.map((field, index) => {
                  // Find the initial value for the field
                  // Co-erce empty strings to null
                  const initialValue =
                    props.initialValues[field.fieldName] === ""
                      ? null
                      : props.initialValues[field.fieldName];

                  // Calculate the editability of the field
                  const isEditable =
                    ("editabilityFn" in field &&
                      field.editabilityFn?.({
                        initialValue,
                        formValues: treatInvalidValuesAsUndefined(
                          props.strictFieldSchemas,
                          values,
                        ),
                      })) ??
                    true;

                  return (
                    <Field
                      key={field.fieldName}
                      field={field}
                      autoFocus={index === indexOfFirstVisibleWritableField}
                      isEditable={isEditable}
                      incidentWorkItem={props.incidentWorkItem}
                      {...formikProps}
                    />
                  );
                })}
                <Box
                  sx={{
                    width: "100%",
                    background: "white",
                  }}
                >
                  <Footer
                    onCancel={{
                      label:
                        props.onAutoSave === undefined
                          ? tk("multiPageForm.cancel")
                          : tk("common.close"),
                      color:
                        props.onAutoSave === undefined ? "error" : "primary",
                      handler: props.onCancel,
                    }}
                    onNext={{
                      handler: async () => {
                        // Perform validation on all fields.
                        const fieldErrors = await formikProps.validateForm();
                        debug("Field errors", fieldErrors);

                        // See if any fields on this page are invalid.
                        const fieldErrorsOnPage = Object.keys(
                          fieldErrors,
                        ).filter((field) =>
                          visibleFieldsOnPage
                            .map((field) => field.fieldName)
                            .includes(field),
                        );
                        const pageIsValid = fieldErrorsOnPage.length === 0;

                        if (pageIsValid) {
                          debug("Page is valid");

                          // Advance to the nearest page with visible fields
                          let nextPageIndex: number | null = null;
                          for (
                            let considerPageIndex = pageIndex + 1;
                            considerPageIndex < props.pages.length;
                            considerPageIndex++
                          ) {
                            if (visiblePages[considerPageIndex]) {
                              nextPageIndex = considerPageIndex;
                              break;
                            }
                          }

                          if (nextPageIndex === null) {
                            await onCleanupAndSubmit(values);
                          } else {
                            // Record that we have visited this page
                            setVisitedPageIndexes([
                              ...visitedPageIndexes,
                              pageIndex,
                            ]);

                            // Move to the next page
                            setPageIndex(nextPageIndex);
                          }
                        } else {
                          debug("Page is invalid", fieldErrorsOnPage);

                          // Touch all fields on the page so that errors are shown
                          await formikProps.setTouched(
                            fieldsOnPage.reduce(
                              (acc, field) => ({
                                ...acc,
                                [field.fieldName]: true,
                              }),
                              {},
                            ),
                          );
                        }
                      },
                    }}
                  >
                    <AutoSave
                      debug={debug}
                      debugPortalRef={autoSaveDebugRef}
                      schema={props.schema}
                      strictFieldSchemas={props.strictFieldSchemas}
                      onAutoSave={props.onAutoSave}
                    />
                  </Footer>
                </Box>
                {debug.enabled && <Box ref={autoSaveDebugRef} />}
              </Stack>
            );
          }}
        </Formik>
      )}
    </FormWrapper>
  );
}
